import type { ICreateDocumentConditionCmd } from "@/data/commands/document/CreateDocumentConditionCmd";
import { Factory } from "@/data/dao/Factory";
import { type IDataSource, parseCollection } from "@/data/dao/service/BaseServiceDao";
import { DocumentDao } from "@/data/dao/service/DocumentDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import type { IDocumentCondition } from "@/data/model/Document";
import { Weekday } from "@/data/model/Enums";
import type { IRoom } from "@/data/model/Room";
import { Misc } from "@/data/util/Misc";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { UiNotification } from "@/portal/ui/Notification";

export class DocumentConditionWindow extends BaseModal {
    private $form: JQuery;

    private roomSelect: Select<IRoom>;
    private fromDateInput: DatePicker;
    private untilDateInput: DatePicker;
    private fromTimeInput: TimePicker;
    private untilTimeInput: TimePicker;

    private weekdaySelect: Select<Weekday>;

    private $submitBtn: JQuery;

    private documentId: string;
    private model: IDocumentCondition;

    public onSaved?: { (translation: IDocumentCondition) };
    public onRemoved?: { () };

    constructor(documentId: string, model?: IDocumentCondition, roomDataSource?: IDataSource<IRoom>) {
        super(
            model === undefined
                ? i18n("modal.documentCondition.titleCreate")
                : i18n("modal.documentCondition.titleEdit"),
            "modal-lg",
            true
        );

        this.documentId = documentId;
        this.model = model!;
        this.$element.addClass("condition");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const limitSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        limitSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">Limitierung</h3></div></div>'
        );

        const roomGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.room") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(limitSection)
            .find("> .col-xl-6");
        this.roomSelect = new Select<IRoom>(
            undefined,
            roomDataSource === undefined ? Factory.getInstance(RoomDao) : roomDataSource,
            (c) => c.name,
            false,
            i18n("modal.documentCondition.allRooms")
        ).renderTo(roomGroup);
        this.roomSelect.setValue(model !== undefined ? model.roomId : undefined);

        const dateGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.documentCondition.timeRange") +
                "</label></div>"
        ).appendTo(limitSection);
        dateGroup.append(
            '<div class="col-xl-1 text-right"><div class="splithint">' +
                i18n("general.timeFormat.from") +
                "</div></div>"
        );
        const fromG1 = $('<div class="col-xl-2" />').appendTo(dateGroup);

        this.fromDateInput = new DatePicker().renderTo(fromG1);
        this.fromDateInput.setValue(model !== undefined ? model.dateFrom : undefined);
        dateGroup.append(
            '<div class="col-xl-1 text-right"><div class="splithint">' +
                i18n("general.timeFormat.until") +
                "</div></div>"
        );
        const untilG1 = $('<div class="col-xl-2" />').appendTo(dateGroup);
        this.untilDateInput = new DatePicker().renderTo(untilG1);
        this.untilDateInput.setValue(model !== undefined ? model.dateUntil : undefined);

        //bind range behaviour
        this.fromDateInput.onChange = (nv) => {
            this.untilDateInput.setStartDate(nv);
        };

        this.untilDateInput.onChange = (nv) => {
            this.fromDateInput.setEndDate(nv);
        };

        this.$form.append(
            '<div class="kt-separator kt-separator--border-dashed kt-separator--portlet-fit kt-separator--space-lg" />'
        );
        const recuringSection = $('<div class="kt-section kt-section--last"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        recuringSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.documentCondition.recurrence") +
                "</h3></div></div>"
        );

        const weekdayGroup = $(
            '<div class="form-group row weekdays"><label class="col-xl-4 col-form-label">' +
                i18n("general.timeFormat.weekdays") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(recuringSection)
            .find("> .col-xl-6");
        const sel = $("<select />").appendTo(weekdayGroup);
        Misc.eachEnum(Weekday, (i, e) => {
            sel.append('<option value="' + i + '">' + i18n("enums.Weekday." + e) + " </option>");
        });

        this.weekdaySelect = Select.createFromSelect(sel, undefined, undefined, false, undefined, undefined, true);
        this.weekdaySelect.setValues(model !== undefined ? model.weekdays : []);

        const timeGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.documentCondition.time") +
                "</label></div>"
        ).appendTo(recuringSection);
        timeGroup.append(
            '<div class="col-xl-1 text-right"><div class="splithint">' +
                i18n("general.timeFormat.from") +
                "</div></div>"
        );
        const fromG = $('<div class="col-xl-2" />').appendTo(timeGroup);

        this.fromTimeInput = new TimePicker(undefined, { showSeconds: false, maxHours: 24 }).renderTo(fromG);
        this.fromTimeInput.setValue(model !== undefined ? model.timeFrom : undefined);

        timeGroup.append(
            '<div class="col-xl-1 text-right"><div class="splithint">' +
                i18n("general.timeFormat.until") +
                "</div></div>"
        );
        const untilG = $('<div class="col-xl-2" />').appendTo(timeGroup);
        this.untilTimeInput = new TimePicker(undefined, { showSeconds: false, maxHours: 24 }).renderTo(untilG);
        this.untilTimeInput.setValue(model !== undefined ? model.timeUntil : undefined);

        if (this.model !== undefined) {
            const deleteBtn = $('<button class="btn btn-clean btn-small">' + i18n("general.remove") + "</button>")
                .appendTo(this.$footer!)
                .click((e) => {
                    e.preventDefault();

                    deleteBtn
                        .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                        .prop("disabled", true);
                    Factory.getInstance<DocumentDao>(DocumentDao)
                        .removeCondition(this.documentId, this.model.id)
                        .then(() => {
                            this.close();
                            if (this.onRemoved !== undefined) this.onRemoved();
                        })
                        .catch((ex) => {
                            logError(ex);
                            deleteBtn
                                .removeClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                                .prop("disabled", false);
                            alert(i18n("general.failedRemove"));
                        });
                });
        }

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private onSubmit() {
        const cmd: ICreateDocumentConditionCmd = {
            weekdays: parseCollection<Weekday>(this.weekdaySelect.getValues(), (c) => parseInt(c)),
            roomId: this.roomSelect.getValue(),
            dateFrom: this.fromDateInput.getValue(),
            dateUntil: this.untilDateInput.getValue(),
            timeFrom: this.fromTimeInput.getValue(),
            timeUntil: this.untilTimeInput.getValue(),
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (cmd.weekdays.length === 0) {
            hasError = true;
            this.weekdaySelect.setError(true);
        }

        if (cmd.dateFrom !== undefined && cmd.dateUntil !== undefined && cmd.dateFrom > cmd.dateUntil) {
            hasError = true;
            this.fromDateInput.setError(true);
            this.untilDateInput.setError(true);
        }

        if (
            cmd.timeFrom !== undefined &&
            cmd.timeUntil !== undefined &&
            this.fromTimeInput!.getSeconds()! >= this.untilTimeInput!.getSeconds()!
        ) {
            hasError = true;
            this.fromTimeInput.setError(true);
            this.untilTimeInput.setError(true);
        }

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        let prom: Promise<IDocumentCondition>;
        if (this.model === undefined)
            prom = Factory.getInstance<DocumentDao>(DocumentDao).createCondition(this.documentId, cmd);
        else prom = Factory.getInstance<DocumentDao>(DocumentDao).updateCondition(this.documentId, this.model.id, cmd);

        prom.then((m) => {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

            this.close();
            if (this.onSaved !== undefined) this.onSaved(m);
        }).catch((ex) => {
            logError(ex);
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

            UiNotification.showException(i18n("modal.documentCondition.failedToSave"));
        });
    }
}
